import { useEffect, useState } from "react";
import { X } from "lucide-react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import {
  FilterValueInput,
  OperatorSelect,
  OPERATORS_BY_TYPE,
  type FieldOption,
  type FilterCondition,
  type FilterValueType,
} from "./AdvancedFilters";
import { resolveChipIcon, type ActiveFilter } from "./filterTypes";
import type { PipelineStatus, ProjectMember } from "@/api/types";

interface FilterChipProps {
  filter: ActiveFilter;
  /** Resolved field metadata for the field this chip represents.
   *  Null means the chip is non-editable (e.g. an unrecognised
   *  field) — the body opens nothing, only × removes. */
  field: FieldOption | null;
  /** The chip's "true" condition for the editor — for tab/status/
   *  score chips we synthesize one from the page-level state. */
  editableCondition: FilterCondition | null;
  slug: string;
  members: ProjectMember[];
  pipelineStatuses: PipelineStatus[];
  /** Save callback — receives the edited operator + value. The
   *  parent decides what to do with them based on `filter.source`
   *  (tab → switch tab; status → patch pills; score → patch slider;
   *  advanced → patch the matching FilterCondition by id). */
  onEditCommit: (next: { operator: string; value: unknown }) => void;
  onRemove: () => void;
}

/** A single chip in the FilterChipsBar. Click on the body opens an
 *  inline editor popover seeded with this chip's current values;
 *  the × button to the right removes the chip. */
export function FilterChip({
  filter,
  field,
  editableCondition,
  slug,
  members,
  pipelineStatuses,
  onEditCommit,
  onRemove,
}: FilterChipProps) {
  const [open, setOpen] = useState(false);
  const editable = field !== null && editableCondition !== null;
  // Enter-only mount animation. Start hidden + slightly offset, flip
  // to visible on the next paint so the browser commits the initial
  // state before the transition kicks in. The motion-safe: prefix on
  // the hidden state means users with reduced-motion preferences see
  // the chip at its final position immediately — no flash.
  const [mounted, setMounted] = useState(false);
  useEffect(() => {
    const id = requestAnimationFrame(() => setMounted(true));
    return () => cancelAnimationFrame(id);
  }, []);

  const Icon = resolveChipIcon(filter, field);

  return (
    <div
      className={cn(
        "inline-flex items-center transition-all duration-200 motion-reduce:transition-none",
        mounted
          ? "translate-y-0 opacity-100"
          : "motion-safe:translate-y-1 motion-safe:opacity-0",
      )}
    >
      <Tooltip>
        <Popover
          open={open}
          onOpenChange={(o) => {
            if (!editable && o) return; // Non-editable chips don't open.
            setOpen(o);
          }}
        >
          <PopoverTrigger asChild>
            <TooltipTrigger asChild>
              <button
                type="button"
                disabled={!editable}
                aria-label={`Edit ${filter.fieldLabel} filter`}
                className={cn(
                  "inline-flex items-center gap-1.5 rounded-l-full px-2.5 py-1 text-xs transition-colors",
                  "bg-slate-100 text-slate-700",
                  "dark:bg-slate-800 dark:text-slate-200",
                  editable && "hover:bg-slate-200 dark:hover:bg-slate-700",
                  !editable && "cursor-default",
                )}
              >
                <Icon
                  className="h-3.5 w-3.5 shrink-0 text-slate-500 dark:text-slate-400"
                  aria-hidden
                />
                <span className="text-slate-700 dark:text-slate-200">
                  {filter.fieldLabel}
                </span>
                <span className="text-slate-500 dark:text-slate-400">
                  {filter.operatorLabel}
                </span>
                <span
                  className="max-w-[200px] truncate font-medium text-slate-900 dark:text-slate-100"
                  title={filter.valueLabel}
                >
                  {filter.valueLabel}
                </span>
              </button>
            </TooltipTrigger>
          </PopoverTrigger>
          {editable && (
            <PopoverContent
              align="start"
              sideOffset={6}
              className="w-[380px] p-3"
            >
              <ChipEditor
                field={field}
                initial={editableCondition}
                slug={slug}
                members={members}
                pipelineStatuses={pipelineStatuses}
                onCancel={() => setOpen(false)}
                onConfirm={(next) => {
                  onEditCommit(next);
                  setOpen(false);
                }}
              />
            </PopoverContent>
          )}
        </Popover>
        {/* Hide the chip-body tooltip when the editor is open — the
         *  popover IS the affordance the tooltip advertises, so
         *  layering them would be noise. Also hide for non-editable
         *  chips since "click to edit" is misleading there. */}
        {editable && !open && (
          <TooltipContent
            side="bottom"
            sideOffset={8}
            className="border border-slate-200 bg-white text-[11px] font-normal text-slate-600 shadow-sm dark:border-slate-700 dark:bg-slate-900 dark:text-slate-300"
          >
            Click to edit · × to remove
          </TooltipContent>
        )}
      </Tooltip>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            onClick={onRemove}
            aria-label={`Remove ${filter.fieldLabel} filter`}
            className={cn(
              "inline-flex h-[26px] items-center rounded-r-full bg-slate-100 px-1.5 text-slate-400 transition-colors",
              "hover:bg-slate-300 hover:text-slate-800",
              "dark:bg-slate-800 dark:text-slate-500 dark:hover:bg-slate-700 dark:hover:text-slate-100",
            )}
          >
            <X className="h-3 w-3" />
          </button>
        </TooltipTrigger>
        <TooltipContent
          side="bottom"
          sideOffset={8}
          className="border border-slate-200 bg-white text-[11px] font-normal text-slate-600 shadow-sm dark:border-slate-700 dark:bg-slate-900 dark:text-slate-300"
        >
          Remove filter
        </TooltipContent>
      </Tooltip>
    </div>
  );
}

interface ChipEditorProps {
  field: FieldOption;
  initial: FilterCondition;
  slug: string;
  members: ProjectMember[];
  pipelineStatuses: PipelineStatus[];
  onCancel: () => void;
  onConfirm: (next: { operator: string; value: unknown }) => void;
}

/** The shared operator+value editor used by both the FilterChip
 *  popover and the AddFilterPopover's second step. Pure form — no
 *  side effects, no fetching beyond what FilterValueInput already
 *  does. */
export function ChipEditor({
  field,
  initial,
  slug,
  members,
  pipelineStatuses,
  onCancel,
  onConfirm,
}: ChipEditorProps) {
  const [operator, setOperator] = useState(initial.operator);
  const [value, setValue] = useState<unknown>(initial.value);

  // Reset local state if the underlying field changes (parent
  // re-keyed us with a different chip). Edge case — most chip-edit
  // popovers stay on one field for their lifetime.
  useEffect(() => {
    setOperator(initial.operator);
    setValue(initial.value);
  }, [initial.field, initial.operator, initial.value]);

  const type = field.type;
  // Synthesise a FilterCondition for FilterValueInput. id is unused
  // by the input — it only reads field / operator / value.
  const synthesized: FilterCondition = {
    id: "chip-edit",
    field: field.value,
    operator,
    value,
  };

  return (
    <div className="space-y-3">
      <div className="text-[11px] font-medium uppercase tracking-wider text-slate-500">
        {field.label}
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <OperatorSelect
          type={type}
          value={operator}
          onChange={(nextOp) => {
            // Switching operators sometimes reshapes the value (e.g.
            // moving from "eq" to "between" needs an array). Reset
            // to the natural default for the new op shape to keep
            // FilterValueInput stable.
            setOperator(nextOp);
            setValue(naturalValueFor(type, nextOp));
          }}
        />
        <FilterValueInput
          condition={synthesized}
          slug={slug}
          type={type}
          members={members}
          pipelineStatuses={pipelineStatuses}
          onChange={(v) => setValue(v)}
        />
      </div>
      <div className="flex justify-end gap-2 pt-1">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={onCancel}
        >
          Cancel
        </Button>
        <Button
          type="button"
          variant="primary"
          size="sm"
          onClick={() => onConfirm({ operator, value })}
        >
          Apply
        </Button>
      </div>
    </div>
  );
}

/** Default value shape for a given (type, operator) — mirrors the
 *  reset rules inside AdvancedFilters' inline editor so the chip
 *  popover doesn't accidentally hand a scalar to a between-tuple
 *  input. */
function naturalValueFor(type: FilterValueType, operator: string): unknown {
  if (operator === "between") return ["", ""];
  if (operator === "in" || operator === "not_in") return [];
  if (operator === "is_empty" || operator === "is_not_empty") return "";
  if (type === "boolean") return false;
  return "";
}

/** Returns the default operator for a given field type — used when
 *  +Add filter creates a fresh condition. Falls back to "eq" for
 *  unknown types so the value editor always renders. */
export function defaultOperatorFor(type: FilterValueType): string {
  return OPERATORS_BY_TYPE[type]?.[0]?.value ?? "eq";
}
